// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Heading, IconRefreshLine, Spinner, Table, Text, Tooltip, View } from '@instructure/ui';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { pdf } from '@react-pdf/renderer';
// Components, Layouts, Pages
import Information from '../home/component/information/Information';
import { DocumentPDF } from './documentPDF/DocumentPdf';
import { BasePagination } from '~/components/common';
// Others
import { ExamStatusEnum } from '~/utils/enum/base';
import { getListExams } from '~/thunks/exam/examThunk';
import { DEFAULT_LIMIT, DEFAULT_LIMIT_PAGE, DEFAULT_PAGE, TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { IProfile } from '~/utils/interface/profile';
import { IExam, IQueryGetExams } from '~/utils/interface/exam';
import { IToast } from '~/utils/interface/toast';
import { generateGuid } from '~/utils/helper/common';
import { getProfile } from '~/thunks/user/userThunk';
import { toastActions } from '~/thunks/toast/toastSlice';
import i18n from '~/thirdParties/i18n';
// Styles, images, icons
import styles from './EducationalProgram.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const EducationalProgram = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { exams, hasMore, isLoading } = useAppSelector((state) => state.examState);
  const userProfile = useAppSelector((state) => state.userState.profile);
  //#endregion Selector

  //#region Declare State
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetExams();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetExams({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const handleGetExams = async (query?: IQueryGetExams) => {
    setErrorMessage(null);

    try {
      await dispatch(
        getListExams(
          query ?? {
            limit: DEFAULT_LIMIT_PAGE,
            page: page,
          }
        )
      ).unwrap();
    } catch (e) {
      setErrorMessage((e as Error).message);
    }
  };

  const handleGetPDFInformation = async () => {
    let pdfExams: IExam[] | undefined;
    let profile: IProfile | undefined;

    try {
      await dispatch(
        getListExams({
          limit: DEFAULT_LIMIT,
        })
      )
        .unwrap()
        .then(async (res) => {
          pdfExams = res.responses;
        });

      await dispatch(getProfile())
        .unwrap()
        .then((resProfile) => {
          profile = resProfile.data;
        });
    } catch (e) {
      console.log((e as Error).message);
    }

    return { exams: pdfExams, profile };
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadPDF = async () => {
    if (!exams.length || !userProfile) {
      const { exams: examData, profile: profileData } = await handleGetPDFInformation();
      if (!exams || !profileData) {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_error_message', { ns: 'common/base' }),
          type: 'error',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
        return;
      }

      handleCreatePDF(examData!, profileData);
      return;
    }

    handleCreatePDF(exams, userProfile);
  };

  const handleCreatePDF = async (examData: IExam[], profileData: IProfile) => {
    await i18n.loadNamespaces(['common/exam']);
    const blob = await pdf(<DocumentPDF exams={examData} profile={profileData} />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'study-program.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderDesktopContent = () => {
    if (isLoading && exams.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            <Button
              onClick={() => handleGetExams()}
              size='medium'
              color='primary'
              margin='x-large 0 0 0'
              renderIcon={<IconRefreshLine />}
            >
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (exams.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' alignItems='center' margin='small 0 0 0'>
            <Text variant='contentSmall'>{t('educational_program_no_data_available', { ns: 'common/home' })}</Text>
          </Flex>
        </div>
      );
    }

    return (
      <Table layout='auto'>
        <Table.Head>
          <Table.Row>
            <Table.ColHeader id='Name'>{t('educational_program_name_field_title')}</Table.ColHeader>
            <Table.ColHeader id='Point'>{t('educational_program_point_field_title')}</Table.ColHeader>
            <Table.ColHeader id='Point'>{t('educational_program_value_field_title')}</Table.ColHeader>
            <Table.ColHeader id='Status'>{t('educational_program_status_field_title')}</Table.ColHeader>
            <Table.ColHeader id='Status'>{t('educational_program_credit_field_title')}</Table.ColHeader>
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {exams.map((exam, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <Tooltip renderTip={exam.moduleName}>
                  <span className={cx('moduleName')}>{exam.moduleName}</span>
                </Tooltip>
              </Table.Cell>

              <Table.Cell>
                <div className={cx('modulePoints')}>
                  <span>{exam.pointValue}</span>
                </div>
              </Table.Cell>

              <Table.Cell>
                <div>
                  <span>{exam.value}</span>
                </div>
              </Table.Cell>

              <Table.Cell>
                <div className={cx('moduleStatus')}>
                  <img
                    src={
                      exam.status === ExamStatusEnum.FAILED
                        ? icons.commonIconStatusFailed
                        : icons.commonIconChangeStatusActive
                    }
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('iconStatus')}
                  />
                  <span>
                    {exam.status === ExamStatusEnum.FAILED
                      ? t('educational_program_failed_status')
                      : t('educational_program_passed_status')}
                  </span>
                </div>
              </Table.Cell>

              <Table.Cell>
                <div>
                  <span>{exam.credits}</span>
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  const renderMobileContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' margin='medium 0'>
            <Button
              onClick={() => handleGetExams()}
              size='medium'
              color='primary'
              margin='x-large 0 0 0'
              renderIcon={<IconRefreshLine />}
            >
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (exams.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center' alignItems='center' margin='small 0 0 0'>
            <Text variant='contentSmall'>{t('educational_program_no_data_available', { ns: 'common/home' })}</Text>
          </Flex>
        </div>
      );
    }

    return (
      <>
        {exams.map((data, index) => (
          <View key={index} as='div' className={cx('card')}>
            <View as='div' className={cx('cardItem')}>
              <span className={cx('cardLabel')}>{t('educational_program_name_field_title')}:</span>
              <span>{data.moduleName}</span>
            </View>

            <View as='div' className={cx('cardItem')}>
              <span className={cx('cardLabel')}>{t('educational_program_point_field_title')}:</span>
              <span>{data.pointValue}</span>
            </View>

            <View as='div' className={cx('cardItem')}>
              <span className={cx('cardLabel')}>{t('educational_program_value_field_title')}:</span>
              <span>{data.value}</span>
            </View>

            <View as='div' className={cx('cardItem')}>
              <span className={cx('cardLabel')}>{t('educational_program_credit_field_title')}:</span>
              <span>
                {t('educational_program_credit_value', {
                  quantity: data.credits,
                })}
              </span>
            </View>

            <View as='div' className={cx('cardItem')}>
              <span className={cx('cardLabel')}>{t('educational_program_status_field_title')}:</span>

              <View as='div' className={cx('cardItem', 'status')}>
                <img
                  src={
                    data.status === ExamStatusEnum.FAILED
                      ? icons.commonIconStatusFailed
                      : icons.commonIconChangeStatusActive
                  }
                  alt='Status Icon'
                  className={cx('iconStatus')}
                />
                <span>
                  {data.status === ExamStatusEnum.FAILED
                    ? t('educational_program_failed_status')
                    : t('educational_program_passed_status')}
                </span>
              </View>
            </View>
          </View>
        ))}
      </>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <div className={cx('leftSection')}>
          <div className={cx('topBox')}>{<Information hideIcons={true} />}</div>
        </div>

        <div className={cx('rightSection')}>
          <Flex justifyItems='space-between' margin='0 0 large 0'>
            <Heading level='h3'>{t('educational_program_title')}</Heading>

            <View className={cx('printPdfDesktop')}>
              <Button color='primary' onClick={handleDownloadPDF}>
                {t('educational_program_download_study_pdf_title')}
              </Button>
            </View>

            <View className={cx('printPdfMobile')}>
              <Tooltip renderTip={t('educational_program_download_study_pdf_title')}>
                <Button onClick={handleDownloadPDF}>
                  <img
                    src={icons.commonIconDownloadPdf}
                    alt={t('common_alt_img', { ns: 'common/base' })}
                    className={cx('downloadIcon')}
                  />
                </Button>
              </Tooltip>
            </View>
          </Flex>

          <div className={cx('tableContainer')}>
            {renderDesktopContent()}
            <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
          </div>

          <div className={cx('tableContainerMobile')}>
            {renderMobileContent()}
            <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationalProgram;
